/**
 * SizilPro — authStore
 * Zustand store for Supabase auth state.
 *
 * Initialized once in main.tsx by calling initAuth().
 * Components read clientId, userId, and role via selectors — never
 * import the Supabase client directly for auth reads.
 */

import { create } from "zustand";
import { supabase } from "@/lib/supabase";
import type { AuthChangeEvent, Session, User } from "@supabase/supabase-js";

// ─── Types ────────────────────────────────────────────────────
export type UserRole = "owner" | "admin" | "member" | "super_admin";

interface ProfileData {
  client_id: string;
  role: UserRole;
  name: string | null;
  email: string | null;
  avatar_url: string | null;
}

interface AuthState {
  // Auth
  user: User | null;
  userId: string | null;
  isLoading: boolean;

  // Profile (from public.users row)
  clientId: string | null;
  role: UserRole | null;
  name: string | null;
  email: string | null;
  avatarUrl: string | null;

  // Internal lifecycle
  initialized: boolean;
  listenerBound: boolean;

  // Actions
  setUser: (user: User | null) => void;
  setProfile: (profile: ProfileData | null) => void;
  clearAuth: () => void;
  initAuth: () => Promise<void>;
  signOut: () => Promise<void>;
}

let authListenerCleanup: (() => void) | null = null;
let initPromise: Promise<void> | null = null;

// ─── Store ────────────────────────────────────────────────────
export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  userId: null,
  isLoading: true,

  clientId: null,
  role: null,
  name: null,
  email: null,
  avatarUrl: null,

  initialized: false,
  listenerBound: false,

  setUser: (user) =>
    set({
      user,
      userId: user?.id ?? null,
    }),

  setProfile: (profile) =>
    set({
      clientId: profile?.client_id ?? null,
      role: profile?.role ?? null,
      name: profile?.name ?? null,
      email: profile?.email ?? null,
      avatarUrl: profile?.avatar_url ?? null,
    }),

  clearAuth: () =>
    set({
      user: null,
      userId: null,
      clientId: null,
      role: null,
      name: null,
      email: null,
      avatarUrl: null,
      isLoading: false,
    }),

  initAuth: async () => {
    const state = get();

    if (state.initialized && state.listenerBound) {
      return;
    }

    if (initPromise) {
      return initPromise;
    }

    initPromise = (async () => {
      set({ isLoading: true });

      try {
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (error) {
          console.warn("[authStore] getSession failed:", error.message);
          get().clearAuth();
        } else {
          await syncFromSession(session);
        }

        if (!get().listenerBound) {
          const {
            data: { subscription },
          } = supabase.auth.onAuthStateChange(
            (event: AuthChangeEvent, session: Session | null) => {
              void handleAuthStateChange(event, session);
            }
          );

          authListenerCleanup = () => {
            subscription.unsubscribe();
          };

          set({ listenerBound: true });
        }

        set({ initialized: true, isLoading: false });
      } finally {
        initPromise = null;
      }
    })();

    return initPromise;
  },

  signOut: async () => {
    try {
      await supabase.auth.signOut();
    } finally {
      get().clearAuth();
    }
  },
}));

async function handleAuthStateChange(
  event: AuthChangeEvent,
  session: Session | null
) {
  const currentUserId = useAuthStore.getState().userId;
  const nextUserId = session?.user?.id ?? null;

  if (event === "SIGNED_OUT" || !session) {
    useAuthStore.getState().clearAuth();
    return;
  }

  // If the same user is already loaded with a clientId, skip the full profile
  // reload regardless of which auth event fired. Supabase can emit SIGNED_IN,
  // USER_UPDATED, or TOKEN_REFRESHED when a tab regains focus after a background
  // token refresh. Without this guard those events set isLoading:true, which
  // unmounts the entire app via __root.tsx and wipes all in-progress form state.
  if (currentUserId === nextUserId && useAuthStore.getState().clientId) {
    useAuthStore.setState({ isLoading: false });
    return;
  }

  useAuthStore.setState({ isLoading: true });

  try {
    await syncFromSession(session);
  } finally {
    useAuthStore.setState({ isLoading: false });
  }
}

async function syncFromSession(session: Session | null) {
  if (!session?.user) {
    useAuthStore.getState().clearAuth();
    return;
  }

  useAuthStore.getState().setUser(session.user);

  const profile = await loadProfile(session.user.id);
  useAuthStore.getState().setProfile(profile);
}

async function loadProfile(userId: string): Promise<ProfileData | null> {
  const { data, error } = await supabase
    .from("users")
    .select("client_id, role, name, email, avatar_url")
    .eq("id", userId)
    .single();

  if (error || !data) {
    console.warn("[authStore] Could not load profile:", error?.message);
    return null;
  }

  return data as ProfileData;
}

// ─── Convenience selectors ────────────────────────────────────
export const selectClientId = (s: AuthState) => s.clientId;
export const selectUserId = (s: AuthState) => s.userId;
export const selectRole = (s: AuthState) => s.role;
export const selectIsOwner = (s: AuthState) =>
  s.role === "owner" || s.role === "super_admin";
export const selectIsLoading = (s: AuthState) => s.isLoading;

// Optional dev helper for HMR/manual resets
export function cleanupAuthStoreListener() {
  if (authListenerCleanup) {
    authListenerCleanup();
    authListenerCleanup = null;
  }

  useAuthStore.setState({
    listenerBound: false,
    initialized: false,
  });
}